import React from 'react'
import { Lock, Headphones, UsersRound } from 'lucide-react'

const Solution = () => {
  const benefits = [
    {
      icon: Lock,
      title: 'Focus Without Fighting Willpower',
      description:
        'Prayer Mode locks Instagram, TikTok, and any distraction apps for 5-60 minutes. Your phone becomes a prayer sanctuary, not a temptation device.',
      stat: '85% complete sessions vs. 34% without app locking',
      bgClass: 'bg-zoe-primary/10',
      textClass: 'text-zoe-primary',
    },
    {
      icon: Headphones,
      title: 'Never Pray Alone Again',
      description:
        '600+ guided audio devotions (5-20 min) teach you how to pray beyond "bless my food." From anxiety to gratitude, follow expert pastors through meaningful conversations with God.',
      stat: 'Avg 4.8 star rating across all devotions',
      bgClass: 'bg-zoe-accent/10',
      textClass: 'text-zoe-accent',
    },
    {
      icon: UsersRound,
      title: 'Accountability That Actually Works',
      description:
        'Join a Prayer Pod (2-10 friends) with a real-time leaderboard. See who prayed today, celebrate 30-day streaks together, and turn spiritual growth into a shared journey.',
      stat: 'Pod members pray 2.3x more consistently',
      bgClass: 'bg-zoe-success/10',
      textClass: 'text-zoe-success',
    },
  ]

  return (
    <section className="section-padding bg-gradient-to-b from-grace-gray to-white">
      <div className="container-custom">
        {/* Headline */}
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-heading-dark mb-6">
            Meet ZoeLive Prayer: Your Phone Becomes Your{' '}
            <span className="text-gradient">Prayer Partner</span>
          </h2>
          <p className="text-lg md:text-xl text-body-gray max-w-4xl mx-auto leading-relaxed">
            ZoeLive Prayer transforms distraction into devotion with a simple 3-tap ritual:
            Lock apps. Play guided prayer. Track your streak. Your Prayer Pod friends
            see your progress, cheer you on, and keep you accountable — all while your
            phone blocks the very apps that used to derail you.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon
            return (
              <div
                key={index}
                className="card text-center animate-on-scroll"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${benefit.bgClass} mb-4`}>
                  <Icon size={32} className={benefit.textClass} />
                </div>
                <h3 className="text-xl font-bold text-heading-dark mb-3">{benefit.title}</h3>
                <p className="text-body-gray leading-relaxed mb-4">{benefit.description}</p>
                <div className="text-sm font-semibold text-zoe-primary bg-zoe-primary/10 rounded-lg py-2 px-4 inline-block">
                  {benefit.stat}
                </div>
              </div>
            )
          })}
        </div>

        {/* Demo Screenshots */}
        <div className="animate-on-scroll">
          <h3 className="text-2xl font-bold text-center text-heading-dark mb-8">
            Real screens. Real results. Real community.
          </h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { title: '12-Day Streak', subtitle: 'Home Screen' },
              { title: 'Prayer Mode Active', subtitle: 'Devotion Playing' },
              { title: 'Pod Leaderboard', subtitle: '6 Members Ranked' },
            ].map((screen, index) => (
              <div key={index} className="card">
                <div className="aspect-[9/19] bg-gradient-to-br from-zoe-primary/20 to-zoe-success/20 rounded-lg mb-4 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-zoe-primary mb-2">{screen.title}</div>
                    <div className="text-sm text-muted-gray">{screen.subtitle}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Solution
